import resilientImg from "@/assets/tesla-powerwall-3.webp";
import electricImg from "@/assets/hero-aerial-solar-roof.webp";
import restorationImg from "@/assets/removal-reinstall-solar.webp";

export interface PackageFaq {
  q: string;
  a: string;
}

export interface PackageStep {
  title: string;
  body: string;
  duration?: string;
}

export interface PackageItem {
  title: string;
  body: string;
}

export type PackageSlug =
  | "resilient-home"
  | "electric-home"
  | "solar-drive"
  | "backup-charge"
  | "power-ready-home"
  | "roof-solar-restoration"
  | "solar-rescue-care"
  // legacy slug — redirects to roof-solar-restoration, kept for lookup only
  | "restoration";

export type PackageGoal =
  | "outages"
  | "ev"
  | "roof-solar"
  | "solar-problems"
  | "future-ready";

export interface PackageHubspot {
  type_of_service: string;
  project_type?: string;
}

export interface BundlePackage {
  slug: PackageSlug;
  /** Friendly marketing name shown on cards and grids. */
  name: string;
  /**
   * Optional descriptive headline used for the page H1 / SEO title / meta
   * when the friendly card name would be ambiguous with a product name
   * (e.g. "Solar + Drive" vs SPAN Drive). Falls back to `name`.
   */
  publicH1?: string;
  stackLabel: string;
  tags: string[];
  tagline: string;
  overview: string;
  heroImage: string;
  /** Legacy — no longer rendered publicly. Kept for internal reference only. */
  priceRange?: string;
  priceDisclaimer?: string;
  whoItsFor: string[];
  bestFor?: string;
  whyOneTeam: PackageItem[];
  included: PackageItem[];
  process: PackageStep[];
  equipment?: { category: string; options: string }[];
  warranty: string;
  faqs: PackageFaq[];
  crossSell: { text: string; slug: PackageSlug };
  seo: { title: string; description: string };
  group: "complete" | "focused";
  goals: PackageGoal[];
  hubspot: PackageHubspot;
  /** If set, this slug redirects to `redirectsTo` and is not rendered. */
  legacy?: boolean;
  redirectsTo?: PackageSlug;
  /** Focused package that hands off to another flow instead of the builder. */
  handoff?: { href: string; label: string };
}

const CTA_BOOK = "Personalized scope — tailored after a quick project review.";

export const PACKAGES: BundlePackage[] = [
  {
    slug: "resilient-home",
    name: "The Resilient Home",
    stackLabel: "Roof + Solar + Battery Backup",
    tags: ["Roofing", "Solar", "Battery"],
    tagline:
      "Plan a new roof, solar and battery backup together, with backup loads and capacity defined for your home.",
    overview:
      "If your roof is within a few years of replacement and you're considering solar, sequencing matters. Done as one project, the roof is built for the array from day one — roof penetrations and flashing included in the roofing and solar plan, with separate workmanship and manufacturer terms defined in writing.",
    heroImage: resilientImg,
    bestFor: "Homes needing a new roof plus solar and battery backup, done as one coordinated build.",
    group: "complete",
    goals: ["outages", "roof-solar", "future-ready"],
    hubspot: { type_of_service: "Solar Installation" },
    whoItsFor: [
      "Your roof is 15+ years old or due for replacement within the next five years",
      "You want real backup power for outages and PSPS shutoffs, not just lower bills",
      "You don't want to pay to remove and reinstall panels later because solar went on a roof that wasn't ready",
    ],
    whyOneTeam: [
      { title: "Defined warranty responsibilities", body: "Roofing, solar, and electrical performed under one license — CSLB #999485 (B, C-10, C-39)." },
      { title: "A roof designed for the array", body: "Attachment layout, ventilation, and flashing planned before the first shingle — not retrofitted after." },
      { title: "One coordinated schedule", body: "Roofing, installation, inspections and utility approval are planned together; timing depends on the site and approvals." },
    ],
    included: [
      { title: "Full tear-off and deck inspection", body: "Existing roofing removed, decking inspected and repaired, new synthetic underlayment." },
      { title: "New roof system", body: "Architectural, presidential, or tile with new flashings, drip edge, and proper attic ventilation." },
      { title: "Solar sized for ~125% of your usage", body: "Production modeled from your actual utility data, with headroom for an EV or future electrification." },
      { title: "Customized home battery backup", body: "Tesla Powerwall, Enphase IQ, or Franklin WH — backup for the circuits that matter most, sized after a load review." },
      { title: "All electrical and interconnection", body: "Wiring, monitoring, utility interconnection, and Permission to Operate." },
      { title: "Coordinated permitting and inspections", body: "Building, solar, and electrical permits pulled and inspected as one project." },
    ],
    process: [
      { title: "Site assessment", body: "Roof measure, attic and deck check, shade analysis, and panel evaluation." },
      { title: "Design and written proposal", body: "Roof spec, array layout, and battery sizing in one proposal." },
      { title: "Permits and approvals", body: "City permits, utility interconnection, HOA documents." },
      { title: "Roof installation", body: "Tear-off to finished roof." },
      { title: "Solar and battery installation", body: "Racking, panels, battery, and electrical." },
      { title: "Inspection, PTO, commissioning", body: "City sign-off, utility PTO, and a monitoring walkthrough." },
    ],
    equipment: [
      { category: "Roofing", options: "Architectural and presidential shingle lines or tile — samples at your assessment" },
      { category: "Battery", options: "Tesla Powerwall, Enphase IQ, Franklin WH" },
    ],
    warranty: "Scope-specific terms: roofing workmanship, solar workmanship, and manufacturer coverage (25-year panel performance, 10-year battery) — all under CSLB #999485.",
    faqs: [
      { q: "Can you put solar on my existing roof instead?", a: "If your roof has 10+ years of life left, yes — The Electric Home is built for that. Otherwise, replacing first avoids paying to remove and reinstall panels for a re-roof later." },
      { q: "What actually happens during an outage?", a: "The battery takes over your backed-up circuits automatically, and your solar recharges it during the day." },
      { q: "Will I need a main panel upgrade?", a: "Sometimes. We evaluate at the site visit and include it up front if needed — no mid-project surprise." },
      { q: "How long does the whole project take?", a: "Timing depends on roof condition, design, permits, equipment and utility approvals. The written project plan defines the sequence and schedule." },
    ],
    crossSell: { text: "Already driving electric or planning to? See The Electric Home.", slug: "electric-home" },
    seo: {
      title: "Roof + Solar + Battery Package | The Resilient Home | Magic Builders",
      description: "Replace your roof and add solar plus whole-home battery backup as one coordinated project in Southern California. Design, permitting, and scheduling are coordinated as one project.",
    },
  },
  {
    slug: "electric-home",
    name: "The Electric Home",
    stackLabel: "Solar + Battery + EV Charger + Panel Upgrade",
    tags: ["Solar", "Battery", "EV", "Electrical"],
    tagline: "Everything you need to go fully electric — designed in one coordinated build instead of four separate trips.",
    overview: "Solar, storage, EV charging, and the panel capacity to run it all — engineered from a single load calculation. One design, one permit package, one inspection track.",
    heroImage: electricImg,
    bestFor: "Homes going all-electric: solar + battery + EV charging + panel capacity in one coordinated build.",
    group: "complete",
    goals: ["ev", "outages", "future-ready"],
    hubspot: { type_of_service: "Solar Installation" },
    whoItsFor: [
      "You drive an EV (or one's on the way) and a standard wall outlet isn't cutting it",
      "Your panel is at or near capacity — older 100A service or no breaker space left",
      "You're on NEM 3.0 or time-of-use rates, where a battery is what makes the solar math work",
    ],
    whyOneTeam: [
      { title: "One load calculation drives everything", body: "Charger amperage, battery capacity, and solar size designed together." },
      { title: "One permit set, one inspection track", body: "Four scopes move through the city as a single project." },
      { title: "Shared wiring and conduit runs", body: "Trenching, conduit, and panel work happen once." },
    ],
    included: [
      { title: "Solar sized for ~125% offset", body: "Covers today's usage with headroom for EV charging." },
      { title: "Customized home battery backup", body: "Tesla Powerwall, Enphase IQ, or Franklin WH — backup for the circuits that matter most, key to NEM 3.0 savings." },
      { title: "Level 2 EV charger", body: "Hardwired 48A — NACS or J1772 to match your vehicle." },
      { title: "200A main panel upgrade if needed", body: "Included in the written proposal when required by the assessment." },
      { title: "Monitoring, interconnection, PTO", body: "Full utility paperwork and system walkthrough." },
    ],
    process: [
      { title: "Site assessment with load calc", body: "Usage review, panel evaluation, charger location, shade analysis." },
      { title: "Design and written proposal", body: "All four scopes engineered and priced as one project total." },
      { title: "Permits and utility approvals", body: "Handled by us." },
      { title: "Installation", body: "Solar, battery, charger, and any panel work in one coordinated build." },
      { title: "Inspection, PTO, walkthrough", body: "City inspection, Permission to Operate, and setup with you." },
    ],
    equipment: [
      { category: "Battery", options: "Tesla Powerwall, Enphase IQ, Franklin WH" },
      { category: "EV charger", options: "Tesla Wall Connector, ChargePoint, Wallbox — matched to your vehicle" },
    ],
    warranty: "Solar and electrical workmanship plus manufacturer coverage (25-year panel, 10-year battery), all under CSLB #999485.",
    faqs: [
      { q: "Do I really need a 200A panel upgrade?", a: "Not always. The load calculation tells us; if needed, it's in the quote from day one." },
      { q: "Why add a battery instead of just solar?", a: "Under NEM 3.0, exported solar earns far less. A battery stores midday production for expensive evening hours." },
      { q: "Which charger works with my car?", a: "We install NACS and J1772 and match the hardware to what you drive." },
    ],
    crossSell: { text: "Roof getting tired too? Bundle it — see The Resilient Home.", slug: "resilient-home" },
    seo: {
      title: "Solar + Battery + EV Charger Package | The Electric Home | Magic Builders",
      description: "Go fully electric in one coordinated project: solar, battery backup, Level 2 EV charging, and a panel upgrade if needed. Southern California, a site-specific permit and installation plan.",
    },
  },
  {
    slug: "solar-drive",
    name: "Solar + Drive",
    publicH1: "Solar + EV Charging Package",
    stackLabel: "Solar + Level 2 EV Charging",
    tags: ["Solar", "EV"],
    tagline: "Pair new solar with the right home EV charger and the panel capacity to support it.",
    overview: "For homeowners going solar and driving electric at the same time. We size the array around your driving pattern, plan the EV load, and handle panel/load-management evaluation, permits, and commissioning as one job.",
    heroImage: electricImg,
    bestFor: "Homeowners pairing new solar with Level 2 home charging.",
    group: "focused",
    goals: ["ev", "future-ready"],
    hubspot: { type_of_service: "Solar Installation" },
    whoItsFor: [
      "You're going solar and also planning EV charging at home",
      "You want the panel and load-management questions answered up front",
      "You'd rather size everything once than pay to retrofit later",
    ],
    whyOneTeam: [
      { title: "One design, one permit set", body: "Solar and EV charger designed together — one plan, one inspection track." },
      { title: "Load calculation done once", body: "Panel and charger amperage settled before install, not after." },
      { title: "Coordinated commissioning", body: "PTO and charger activation on the same visit." },
    ],
    included: [
      { title: "Solar design sized to your usage + EV", body: "Production modeled from utility data with EV load included." },
      { title: "Level 2 EV charger installation", body: "Hardwired charger — Tesla, ChargePoint, Wallbox, Autel, or FLO." },
      { title: "Panel and load-management evaluation", body: "Confirms capacity, or specifies a load controller / panel change if needed." },
      { title: "Permits and utility interconnection", body: "All paperwork pulled and tracked by us." },
      { title: "Commissioning and PTO", body: "System energized, monitoring set up, EV charging tested." },
    ],
    process: [
      { title: "Site assessment", body: "Panel, garage/charger location, roof, and shade review." },
      { title: "Design and written proposal", body: "Solar layout, charger location, and any panel work in one proposal." },
      { title: "Permits", body: "Building, electrical, and utility interconnection." },
      { title: "Install", body: "Solar array, charger, and any electrical work." },
      { title: "Commissioning", body: "PTO, monitoring, and charger walkthrough." },
    ],
    warranty: "Workmanship warranty on solar and electrical scopes, plus manufacturer coverage on panels, inverters, and charger. CSLB #999485.",
    faqs: [
      { q: "Do I need a panel upgrade to add EV charging?", a: "Sometimes. We check with a load calculation; smart load management can often avoid it." },
      { q: "Which chargers do you install?", a: "Tesla, ChargePoint, Wallbox, Autel, and FLO — matched to your vehicle." },
    ],
    crossSell: { text: "Want backup power too? See Backup + Charge.", slug: "backup-charge" },
    seo: {
      title: "Solar + EV Charging Package | Solar + Drive | Magic Builders",
      description: "New solar plus Level 2 home EV charging designed and installed as one project across Southern California by a licensed C-10 contractor. CSLB #999485.",
    },
  },
  {
    slug: "backup-charge",
    name: "Backup + Charge",
    stackLabel: "Battery Backup + EV Charging",
    tags: ["Battery", "EV"],
    tagline: "Reliable battery backup and a home EV charger — sized, permitted, and commissioned together.",
    overview: "For homes that want outage protection and reliable EV charging without adding solar right now. We size the battery around your backup priorities, plan the charger and any load management, and coordinate the electrical work as one project.",
    heroImage: resilientImg,
    bestFor: "Battery backup plus reliable home EV charging.",
    group: "focused",
    goals: ["outages", "ev", "future-ready"],
    hubspot: { type_of_service: "Battery Storage" },
    whoItsFor: [
      "You want outages covered and EV charging that doesn't trip breakers",
      "You already have solar or aren't adding solar today",
      "You'd rather do one coordinated install than two contractor visits",
    ],
    whyOneTeam: [
      { title: "One load calculation", body: "Battery and charger sized against the same real-world usage." },
      { title: "One permit and inspection", body: "Battery + electrical run through the city as one project." },
      { title: "Optional smart controls", body: "SPAN or EcoFlow Smart Home Panel 2 can add per-circuit priorities during backup." },
    ],
    included: [
      { title: "Customized home backup", body: "Battery sized to your backup priorities and load review — Tesla Powerwall, Enphase IQ, or Franklin WH. Backup for the circuits that matter most; final backed-up loads confirmed after load review." },
      { title: "Level 2 EV charger", body: "Hardwired charger matched to your vehicle." },
      { title: "Load calculation and panel evaluation", body: "Confirms capacity and specifies any panel or load-controller work." },
      { title: "Optional smart energy controls", body: "SPAN panel or EcoFlow Smart Home Panel 2 for circuit-level priorities." },
      { title: "Permits, commissioning, monitoring", body: "All paperwork and app setup handled." },
    ],
    process: [
      { title: "Site assessment", body: "Panel, backup priorities, charger location, and any smart-panel fit." },
      { title: "Design and written proposal", body: "Battery sizing, charger placement, panel/controls as one proposal." },
      { title: "Permits", body: "Electrical and any utility paperwork." },
      { title: "Install", body: "Battery, charger, and electrical work." },
      { title: "Commissioning", body: "Backup test, monitoring, and charger walkthrough." },
    ],
    equipment: [
      { category: "Battery", options: "Tesla Powerwall, Enphase IQ, Franklin WH" },
      { category: "EV charger", options: "Tesla, ChargePoint, Wallbox, Autel, FLO" },
      { category: "Smart panel (optional)", options: "SPAN whole-home panel, or EcoFlow Smart Home Panel 2 subpanel" },
    ],
    warranty: "Workmanship warranty on battery and electrical scopes, plus manufacturer coverage on battery and charger. CSLB #999485.",
    faqs: [
      { q: "Broad home coverage or essential circuits?", a: "We'll design both options at the assessment. Broader coverage usually needs more capacity; a targeted essentials scope keeps fridge, lights, internet, and medical loads running. Final backed-up circuits are confirmed after load review." },
      { q: "Can I add solar later?", a: "Yes — we design the electrical scope to accept solar later without rework." },
    ],
    crossSell: { text: "Curious about smart-panel controls? See smart electrical panels.", slug: "power-ready-home" },
    seo: {
      title: "Battery Backup + EV Charging Package | Backup + Charge | Magic Builders",
      description: "Battery backup and a Level 2 EV charger designed and installed as one coordinated electrical project in Southern California.",
    },
  },
  {
    slug: "power-ready-home",
    name: "The Power-Ready Home",
    stackLabel: "Electrical Service + Future-Ready Circuits",
    tags: ["Electrical", "Panel"],
    tagline: "Prepare your home's electrical for an EV, solar, battery, heat pump, or ADU — so you don't pay to redo electrical work twice.",
    overview: "For homeowners planning major additions in the next few years. We run a whole-home load study, review main-panel / service strategy, add whole-home surge protection, and rough in future-ready circuits and conduit so the next project drops in cleanly.",
    heroImage: electricImg,
    bestFor: "Homes preparing for an EV, solar, battery, heat pump, induction, or ADU — without doing electrical work twice.",
    group: "focused",
    goals: ["future-ready", "ev"],
    hubspot: { type_of_service: "Main Panel Upgrade", project_type: "Electrical - Main Panel Upgrade" },
    whoItsFor: [
      "You have older 100A service or a nearly full panel",
      "EV, heat pump, induction, or an ADU is on the roadmap",
      "You want future-ready wiring done once, not rebuilt for each project",
    ],
    whyOneTeam: [
      { title: "One load study drives everything", body: "Panel, service, and future-ready circuits sized against a single plan." },
      { title: "One permit and one inspection", body: "Panel and rough-in performed together." },
      { title: "Optional smart-panel path", body: "SPAN or EcoFlow Smart Home Panel 2 for per-circuit visibility and priorities." },
    ],
    included: [
      { title: "Whole-home load study", body: "Assesses current service and near-term additions." },
      { title: "Main-panel and service strategy", body: "200A/400A upgrade or load management, quoted up front." },
      { title: "Whole-home surge protection", body: "Type-2 SPD installed at the panel." },
      { title: "Future-ready circuits and conduit", body: "Roughed in for EV, battery, HVAC, ADU, or induction." },
      { title: "Optional smart-panel path", body: "SPAN whole-home replacement or EcoFlow Smart Home Panel 2 subpanel." },
    ],
    process: [
      { title: "Site assessment and load study", body: "Panel, loads, and roadmap review." },
      { title: "Written proposal", body: "Panel/service, rough-in, and any smart-panel path in one proposal." },
      { title: "Permits", body: "Electrical permits and utility paperwork." },
      { title: "Install", body: "Panel work and future-ready rough-in." },
      { title: "Sign-off", body: "City inspection and utility close-out." },
    ],
    equipment: [
      { category: "Smart panel (optional)", options: "SPAN whole-home panel, or EcoFlow Smart Home Panel 2 subpanel" },
    ],
    warranty: "Workmanship warranty on electrical scope plus manufacturer coverage on panel and surge equipment. CSLB #999485.",
    faqs: [
      { q: "Do I have to pick SPAN or EcoFlow now?", a: "No — the assessment includes a plain 'traditional / SPAN / EcoFlow / help me choose' path." },
      { q: "Will this actually save me money later?", a: "Yes — running conduit and sizing service once is far cheaper than opening walls and reworking a panel per project." },
    ],
    crossSell: { text: "Comparing SPAN and EcoFlow? See smart electrical panels.", slug: "backup-charge" },
    seo: {
      title: "Future-Ready Electrical Package | The Power-Ready Home | Magic Builders",
      description: "Prepare your home's electrical service for EV, solar, battery, heat pump, or ADU — panel, surge protection, and future-ready circuits in one project.",
    },
  },
  {
    slug: "roof-solar-restoration",
    name: "Roof + Solar Restoration",
    publicH1: "Roof Replacement + Solar Panel Removal & Reinstallation",
    stackLabel: "Roof Replacement + Solar Panel Removal & Reinstall",
    tags: ["Roofing", "Removal & Reinstall", "Solar Service"],
    tagline: "Roof replacement with your existing solar panels removed and reinstalled—planned with our roofing and solar teams.",
    overview: "Magic Builders plans roof replacement and solar removal/reinstallation with our Magic Pro Roofing and Magic Solar Electric teams. We hold B, C-10 and C-39 under CSLB #999485. Equipment condition, attachment details, inspections and separate workmanship terms are defined in the written scope.",
    heroImage: restorationImg,
    bestFor: "Roof replacement under existing solar — removal, re-roof, reinstall, and re-commission by one licensed team.",
    group: "focused",
    goals: ["roof-solar", "solar-problems"],
    hubspot: { type_of_service: "Solar Removal & Reinstall", project_type: "Solar - R&R" },
    whoItsFor: [
      "The roof under your existing solar is leaking or at end of life",
      "You've gotten roofing quotes that exclude panel handling",
      "You're working through a storm or insurance claim on a home with solar",
    ],
    whyOneTeam: [
      { title: "One schedule, minimal downtime", body: "Removal, roofing, and reinstall run back-to-back." },
      { title: "New flashing and mounts, not reused hardware", body: "Every penetration re-flashed into the new roof, warrantied by us." },
      { title: "Documented re-commissioning", body: "Pre-removal production baseline and post-install verification." },
    ],
    included: [
      { title: "Pre-removal system audit", body: "Photo documentation, serial inventory, and a production baseline." },
      { title: "Careful detach and on-site storage", body: "Panels, rail, and wiring protected for the duration of roofing." },
      { title: "Full roof replacement or targeted repairs", body: "Scoped to what the roof actually needs." },
      { title: "New flashings and mounting hardware", body: "Integrated into the new roof system." },
      { title: "Reinstall and re-commission", body: "System re-energized, monitoring reconnected." },
      { title: "Production verification", body: "Output checked against your pre-removal baseline, with documentation." },
    ],
    process: [
      { title: "Assessment and baseline", body: "Roof evaluation, system audit, production baseline." },
      { title: "Coordinated written scope", body: "Roofing and removal/reinstall priced as one number." },
      { title: "Permits", body: "Handled by us where required." },
      { title: "Panel removal", body: "Detach, label, store." },
      { title: "Roof replacement", body: "Tear-off to finished roof." },
      { title: "Reinstall, re-commission, verify", body: "System back up, production checked against baseline." },
    ],
    warranty: "Roofing workmanship covering every penetration, plus workmanship warranty on the reinstallation itself. CSLB #999485.",
    faqs: [
      { q: "How long will my system be offline?", a: "For most homes, under a week total — no gap waiting on a second contractor." },
      { q: "Will my panels produce the same afterward?", a: "We verify output against your pre-removal baseline with documentation." },
      { q: "Is removal and reinstall covered by insurance?", a: "When roof damage is a covered claim, panel detach-and-reset is commonly part of the scope." },
      { q: "Can I upgrade panels while they're down?", a: "It's the cheapest moment to do it — we'll price reinstalling existing equipment vs. an upgrade side by side." },
    ],
    crossSell: { text: "System coming down anyway? Ask about adding battery backup — see The Resilient Home.", slug: "resilient-home" },
    seo: {
      title: "Roof Replacement + Solar Panel Removal & Reinstallation | Magic Builders",
      description: "Roof replacement with your existing solar panels safely removed and reinstalled by one licensed team in Southern California. CSLB #999485.",
    },
  },
  {
    slug: "solar-rescue-care",
    name: "Solar Rescue + Care Readiness",
    stackLabel: "System Takeover + Care Readiness Review",
    tags: ["Solar Rescue", "Care Readiness"],
    tagline: "Support for systems whose installer is unavailable, unresponsive, or no longer servicing the area — with a path to ongoing care once the system is stable.",
    overview: "For homeowners whose original installer is unavailable, unresponsive, or no longer servicing the area, or whose system was never set up with proper monitoring. We assess the system, restore monitoring, document a punch list, and plan repairs. If the system is (or becomes) stable, we then review whether it qualifies for a Solar Care membership; Care is not sold as part of this package and enrollment is not guaranteed. Magic Builders does not assume the original installer's warranty or contractual obligations.",
    heroImage: restorationImg,
    bestFor: "Orphaned, underperforming, or unsupported systems that need a takeover and a clear repair plan.",
    group: "focused",
    goals: ["solar-problems", "outages"],
    hubspot: { type_of_service: "Solar Rescue" },
    // Hands off to the existing rescue configurator. `plan=care` is a non-billable
    // interest signal only — Care is never added to the quote or checkout.
    handoff: { href: "/solar-rescue/configure?service=system-takeover&plan=care&pkg=solar-rescue-care&source=packages", label: "Start Solar Rescue" },
    whoItsFor: [
      "Your original installer is gone, unreachable, or unresponsive",
      "Production has dropped and you don't know why",
      "You want an independent contractor to diagnose and coordinate next steps",
    ],
    whyOneTeam: [
      { title: "Assessment first, care later", body: "Diagnostic pass and documented punch list up front. Care readiness is reviewed only after the system is stable — no bundled subscription." },
      { title: "Monitoring reviewed", body: "Where we can gain customer-authorized portal access, we review current monitoring data to understand what the system is reporting." },
      { title: "Repairs planned, not guessed", body: "Prioritized repair plan with clear next steps, not a scary open-ended bill." },
    ],
    included: [
      { title: "Initial system assessment / takeover", body: "Site inspection, safety check, and inventory." },
      { title: "Monitoring recovery", body: "Existing portal recovered where possible, or new monitoring installed." },
      { title: "Documented punch list", body: "Every issue captured with photos and priority." },
      { title: "Repair planning", body: "Prioritized recommendations with pricing." },
      { title: "Care readiness review", body: "Once the system is stable, we review whether it qualifies for Solar Care and share membership options separately. Not part of this quote." },
    ],
    process: [
      { title: "Configure your rescue", body: "Answer a few questions in our Solar Rescue configurator." },
      { title: "Assessment visit", body: "On-site inspection, monitoring recovery, and punch list." },
      { title: "Repair plan", body: "Documented next steps and quote." },
      { title: "Care readiness review (optional)", body: "Once your system is stable, we review Solar Care eligibility and share membership options separately if it qualifies." },
    ],
    warranty: "Workmanship warranty on any repair work performed. Solar Care membership is separate, offered only after a stabilization/eligibility review, and is not included in this package. CSLB #999485.",
    faqs: [
      { q: "Do you work on any brand of panel or inverter?", a: "Most residential brands, yes. We'll confirm at the assessment." },
      { q: "What if my system needs major repairs?", a: "You'll get a documented, prioritized plan — you decide what to authorize." },
      { q: "Is Solar Care included in this package?", a: "No. Care is offered separately, only after a stabilization and eligibility review. Enrollment is not guaranteed." },
    ],
    crossSell: { text: "Roof also aging? See Roof + Solar Restoration.", slug: "roof-solar-restoration" },
    seo: {
      title: "Solar Rescue + Care Readiness | System Takeover in SoCal | Magic Builders",
      description: "Take over an orphaned or underperforming solar system with a documented punch list and repair plan from a licensed SoCal contractor. Care membership is reviewed separately after stabilization.",
    },
  },
  // ---------------------------------------------------------------
  // Legacy — kept only so /packages/restoration can redirect and any
  // stale internal reference still resolves via getPackage().
  // Not rendered in grid or SEO.
  // ---------------------------------------------------------------
  {
    slug: "restoration",
    name: "The Restoration",
    stackLabel: "Roof Replacement + Solar Removal & Reinstall",
    tags: [],
    tagline: "",
    overview: "",
    heroImage: restorationImg,
    bestFor: "",
    group: "focused",
    goals: [],
    hubspot: { type_of_service: "Solar Removal & Reinstall", project_type: "Solar - R&R" },
    whoItsFor: [],
    whyOneTeam: [],
    included: [],
    process: [],
    warranty: "",
    faqs: [],
    crossSell: { text: "", slug: "roof-solar-restoration" },
    seo: { title: "", description: "" },
    legacy: true,
    redirectsTo: "roof-solar-restoration",
  },
];

/** Note: `CTA_BOOK` is the shared public price-replacement copy. */
export const PACKAGE_PRICE_REPLACEMENT_COPY = CTA_BOOK;

export const getPackage = (slug: string): BundlePackage | undefined =>
  PACKAGES.find((p) => p.slug === slug);

/** Packages rendered in the public grid (excludes legacy). */
export const visiblePackages = (): BundlePackage[] =>
  PACKAGES.filter((p) => !p.legacy);

export const GOAL_LABELS: Record<PackageGoal, string> = {
  outages: "Outages",
  ev: "EV charging",
  "roof-solar": "Roof + solar",
  "solar-problems": "Solar problems",
  "future-ready": "Future-ready electrical",
};

/** Navigation-only normalization; redirect stub stays available to routing. */
export function spotlightPackages(slugs?: PackageSlug[]): BundlePackage[] {
  const requested = slugs ? new Set(slugs.map(slug => getPackage(slug)?.redirectsTo ?? slug)) : undefined;
  return visiblePackages().filter(pkg => !requested || requested.has(pkg.slug));
}
export function packageCardDescription(pkg: BundlePackage): string { return pkg.tagline.trim() || pkg.overview.trim() || "Review this coordinated scope for your property."; }
export function packageCardBestFor(pkg: BundlePackage): string { return pkg.bestFor?.trim() || pkg.whoItsFor.find(line => line.trim()) || "Homes needing a project-specific assessment of this scope."; }
